/**
 * Comparison report domain model and API contract types.
 *
 * Comparison reports capture the differences between two Akeneo sides
 * (source, target, or a staging package) for structural / setup entities.
 */

export type ComparisonSide = 'source' | 'target' | `package:${string}`;

export type ComparisonReportStatus =
  | 'pending'
  | 'running'
  | 'completed'
  | 'failed'
  | 'cancelled';

export type ComparisonChangeType =
  | 'added'
  | 'removed'
  | 'modified'
  | 'unchanged';

/**
 * Entity types included in the first-iteration comparison scope.
 * Products and product models are intentionally excluded.
 *
 * Note: roles, user_groups and users are not exposed by Akeneo SaaS
 * Essentials / Growth APIs. Keep them in the constant for on-premise installs,
 * but the UI excludes them from the default selection.
 */
export const COMPARISON_ENTITY_TYPES: readonly string[] = [
  'categories',
  'families',
  'family_variants',
  'attributes',
  'attribute_groups',
  'attribute_options',
  'channels',
  'locales',
  'currencies',
  'association_types',
  'measurement_families',
  'asset_families',
  'reference_entities',
  'reference_entity_records',
  'roles',
  'user_groups',
  'users',
] as const;

/**
 * Entity types that are selected by default in the UI for a new comparison.
 * These are the types known to be available on Akeneo SaaS Essentials/Growth.
 */
export const COMPARISON_DEFAULT_ENTITY_TYPES: readonly string[] = [
  'categories',
  'families',
  'family_variants',
  'attributes',
  'attribute_groups',
  'attribute_options',
  'channels',
  'locales',
  'currencies',
  'association_types',
  'measurement_families',
  'asset_families',
  'reference_entities',
  'reference_entity_records',
] as const;

export interface ComparisonReportSummary {
  /** Total number of entities inspected across all scoped types. */
  total_entities?: number;
  /** Count of entities only present on the left side. */
  added?: number;
  /** Count of entities only present on the right side. */
  removed?: number;
  /** Count of entities present on both sides but with different hashes. */
  modified?: number;
  /** Count of entities present on both sides with identical hashes. */
  unchanged?: number;
  /** Count of entity types that could not be compared due to an error. */
  errored?: number;
  /** Human-readable summary caption. */
  caption?: string | null;
  /** Details for entity types that were skipped due to timeout or limit. */
  errors?: Array<{ entityType: string; message: string }>;
}

export interface ComparisonReport {
  id: string;
  tenant_id?: string;
  left_side: ComparisonSide;
  right_side: ComparisonSide;
  status: ComparisonReportStatus;
  error_message?: string | null;
  summary?: ComparisonReportSummary | string | null;
  started_at?: string | null;
  completed_at?: string | null;
  created_at?: string;
  updated_at?: string;
}

export interface ComparisonReportEntity {
  report_id: string;
  entity_type: string;
  entity_id: string;
  change_type: ComparisonChangeType;
  left_hash?: string | null;
  right_hash?: string | null;
  diff?: Record<string, unknown> | string | null;
  tenant_id?: string;
}

export interface ComparisonReportFilterOptions {
  status?: ComparisonReportStatus;
  tenantId?: string;
  limit?: number;
  offset?: number;
}

export interface ComparisonReportEntityFilterOptions {
  reportId?: string;
  entityType?: string;
  changeType?: ComparisonChangeType;
  tenantId?: string;
  limit?: number;
  offset?: number;
}
